/* eslint-disable complexity */
import {
  CellContext,
  createColumnHelper,
  Row,
  ColumnDef,
} from '@tanstack/react-table';

import { memo, useState, useRef, useEffect, useMemo } from 'react';

import {
  Box,
  Button,
  Checkbox,
  HStack,
  IconButton,
  MenuItem,
  Spacer,
  Switch,
  Text,
  Tooltip,
  Input,
  useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  faAngleDown,
  faAngleRight,
  faCircle,
  faCoins,
  faEyeSlash,
  faGear,
  faHashtag,
  faLink,
  faPalette,
  faRuler,
  faTag,
  faSitemap,
  faPencil,
  faTableList,
} from '@fortawesome/free-solid-svg-icons';
// local imports
import { useTranslation } from 'react-i18next';
import {
  ColumnType,
  CurrencyEnum,
  ElementIdentifierDbidType,
  OrderColumnDeepFragment,
  OrderStatus,
  Unit,
} from '../../../../gql/graphql';
import OrderBadge from '../../../common/OrderBadge';
import { RowSelectHeader } from '../../../table/RowSelect';
import { ReactComponent as ShapeLabelIcon } from '../../../../assets/icons/shape-label-icon.svg';

import { orderStatusColorMapper } from '../../../../hooks/order';
import {
  QuantityWithUnits,
  ResolvedQuantity,
} from '../../../../domain/property-types';

import { formatQuantity } from '../../../../utils/quantity-utils';
import CircleIcon from '../../../common/CircleIcon';

import SparkelIcon from '../../../common/icon/SparkelIcon';

import { columnMapping, columnTypeToId } from '../utils';
import { useProjectHistory } from '../../project-history/hooks/useProjectHistory';
import DiffNumber from '../../project-history/DiffNumber';
import { DiffText } from '../../project-history/DiffText';
import { DiffLinkedElementCount } from '../../project-history/DiffLinkedElementCount';
import { getQuantityAndCustomDiff } from '../../order-entries-panel/DynamicQuantityCell';
import { getAggEntryDiffPath } from '.';
import { NumericCell, NumericCellMeta } from 'src/components/table/Table';
import { ReactComponent as SigmaIcon } from 'src/assets/icons/sigma-icon.svg';
import { formatUnit } from 'src/utils/unit-utils';
import { ResolvedCustomField } from 'src/domain/resolve-order-entry';

export type OrderEntryTableRow = {
  id: string;
  thisOrderId: string;
  name: string;
  resolvedQuantity: ResolvedQuantity | null;
  unit?: Unit | null;
  unitPrice: number;
  currency: CurrencyEnum;
  parentOrder: {
    id: string;
    name: string;
    status: OrderStatus;
    orderColumns: {
      id: string;
      name: string;
    }[];
    labels: { key: string; value: string }[];
  };
  projectId: string;
  updatedAt: string;
  createdAt: string;
  linkedCount: number;
  total: number;
  customFields: Array<ResolvedCustomField & { orderColumnName: string }>;
  resolvedElementIdentifiersDbid?: ElementIdentifierDbidType[];
  subRows?: OrderEntryTableRow[];
  color?: string;
  // Allow for item groups to be arbitrarily inserted
  itemGroups?: Array<Record<string, string>>;
};

function UnitPriceCell(
  props: CellContext<OrderEntryTableRow, number>
): React.ReactElement {
  const numericCellProps: typeof props = {
    ...props,
    column: {
      ...props.column,
      columnDef: {
        ...props.column.columnDef,
        meta: {
          ...props.column.columnDef.meta,
          numberFormatOptions: { minimumFractionDigits: 2 },
        } as NumericCellMeta,
      },
    },
  };
  const { isHistoryDrawerOpen, diff } = useProjectHistory();
  const { row, table } = props;
  const rows = table.getRowModel().rows;
  const orderId = row.original.thisOrderId; // This is the id from the original entry, not the current aggregated order entry
  // We'll also do another pass so that we can be able to abstract out things to avoid repetition
  const unitPriceDiff = useMemo(() => {
    if (!isHistoryDrawerOpen || !diff) return;
    const diffSubKey = getAggEntryDiffPath(rows, row.id);
    const diffKey = `project.orders.${orderId}.orderEntries.${diffSubKey}.unitPrice`;
    const unitPriceDiff = diff[diffKey];
    if (unitPriceDiff) {
      return {
        old: unitPriceDiff.old || 0,
        new: unitPriceDiff.new || 0,
      };
    }
  }, [rows, isHistoryDrawerOpen, diff, row.id, orderId]);

  if (unitPriceDiff) {
    return (
      <Box alignSelf="flex-end" paddingRight={2}>
        <DiffNumber newValue={unitPriceDiff.new} oldValue={unitPriceDiff.old} />
      </Box>
    );
  }
  return <NumericCell {...numericCellProps} />;
}

function TotalPriceCell(
  props: CellContext<OrderEntryTableRow, number>
): React.ReactElement {
  const numericCellProps: typeof props = {
    ...props,
    column: {
      ...props.column,
      columnDef: {
        ...props.column.columnDef,
        meta: {
          ...props.column.columnDef.meta,
          numberFormatOptions: { minimumFractionDigits: 2 },
        } as NumericCellMeta,
      },
    },
  };

  const { isHistoryDrawerOpen, diff } = useProjectHistory();
  const { row, table } = props;
  const rows = table.getRowModel().rows;
  const orderId = row.original.thisOrderId;
  const totalDiff = useMemo(() => {
    if (!isHistoryDrawerOpen || !diff) return;
    const diffSubKey = getAggEntryDiffPath(rows, row.id);
    const diffKey = `project.orders.${orderId}.orderEntries.${diffSubKey}.unitPrice`;
    const topLevelQtyKey = `project.orders.${orderId}.orderEntries.${diffSubKey}.resolvedQuantity`;
    const nestedQtyKey = `${topLevelQtyKey}.value`;
    const unitPriceDiff = diff[diffKey];
    // these are mutually exclusive, can't have both concurrently
    const qtyDiff = diff[topLevelQtyKey] || diff[nestedQtyKey];
    let oldQtyVal =
      typeof row.original.resolvedQuantity?.value === 'number'
        ? row.original.resolvedQuantity?.value
        : 0;
    let oldUnitPrice = row.original.unitPrice ?? 0;
    let newQtyVal = oldQtyVal;
    let newUnitPrice = oldUnitPrice;
    if (unitPriceDiff) {
      newUnitPrice = unitPriceDiff.new ?? 0;
      oldUnitPrice = unitPriceDiff.old ?? 0;
    }
    if (qtyDiff) {
      newQtyVal = qtyDiff.new ?? 0;
      oldQtyVal = qtyDiff.old ?? 0;
    }
    return {
      old: oldQtyVal * oldUnitPrice,
      new: newQtyVal * newUnitPrice,
    };
  }, [rows, isHistoryDrawerOpen, diff, row.id, orderId]);

  if (totalDiff) {
    return (
      <Box alignSelf="flex-end" paddingRight={2}>
        <DiffNumber newValue={totalDiff.new} oldValue={totalDiff.old} />
      </Box>
    );
  }
  return <NumericCell {...numericCellProps} />;
}

export type LinkedCountMeta = {
  onSelectLinkedElements: (
    event: React.MouseEvent,
    row: Row<OrderEntryTableRow>
  ) => void;
};

type ColumnHeaderProps = {
  icon: React.ReactNode;
  name: string;
  isRenaming?: boolean;
  onRename?: (newName: string) => void;
  onRenameCancel?: () => void;
  translation?: (key: string) => string;
};

const ColumnHeader = memo(
  ({ icon, name, isRenaming, onRename, onRenameCancel }: ColumnHeaderProps) => {
    const [inputValue, setInputValue] = useState(name);
    const inputRef = useRef<HTMLInputElement>(null);
    const renameColor = useColorModeValue('gray.800', 'gray.100');

    useEffect(() => {
      if (isRenaming && inputRef.current) {
        setInputValue(name);
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isRenaming, name]);

    const handleRenameComplete = () => {
      const trimmedValue = inputValue.trim();
      // Only rename if there's a value and it's different from the original name
      if (onRename && trimmedValue && trimmedValue !== name) {
        onRename(trimmedValue);
      } else if (onRenameCancel) {
        onRenameCancel();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        handleRenameComplete();
      } else if (e.key === 'Escape' && onRenameCancel) {
        onRenameCancel();
      } else if (e.key === ' ') {
        // Prevent space key from triggering other actions
        e.stopPropagation();
      }
    };

    return (
      <HStack spacing={1} width="100%">
        {icon}
        {isRenaming ? (
          <Input
            ref={inputRef}
            size="xs"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleRenameComplete}
            onKeyDown={handleKeyDown}
            autoFocus
            variant="unstyled"
            flex={1}
            borderRadius="0"
            color={renameColor}
          />
        ) : (
          <Text noOfLines={1} wordBreak="break-all">
            {name}
          </Text>
        )}
      </HStack>
    );
  }
);

ColumnHeader.displayName = 'ColumnHeader';

const TitleCell = memo(
  ({ getValue, row, table }: CellContext<OrderEntryTableRow, unknown>) => {
    let value = getValue<string>();
    const { isHistoryDrawerOpen, diff } = useProjectHistory();
    const rows = table.getRowModel().rows;
    const orderId = row.original.thisOrderId;
    const textDiff = useMemo(() => {
      if (!isHistoryDrawerOpen || !diff) return;
      // name changes can be nested because the top-level entries in ordinary tables can be nested in grouped aggregated entries
      const subKey = getAggEntryDiffPath(rows, row.id);
      const nameKey = `project.order.${orderId}.orderEntries.${subKey}.name`;
      const nameDiff = diff[nameKey];
      if (nameDiff) {
        return {
          old: nameDiff.old,
          new: nameDiff.new,
        };
      }
    }, [isHistoryDrawerOpen, diff, rows, orderId]);
    const showDefaultGroupedValue =
      (row.original as any).__isGrouped && value.startsWith('Missing');
    return (
      <Text
        noOfLines={1}
        wordBreak={'break-all'}
        paddingLeft={2 * row.depth}
        color={showDefaultGroupedValue ? 'gray.400' : 'inherit'}
      >
        {textDiff ? (
          <DiffText oldValue={textDiff.old} newValue={textDiff.new} />
        ) : (
          value
        )}
      </Text>
    );
  }
);

TitleCell.displayName = 'TitleCell';

const TextCell = memo(
  ({
    getValue,
    column,
    table,
    row,
  }: CellContext<OrderEntryTableRow, unknown>) => {
    // This is used by Custom, Parent order Labels and Item Groups
    // we can detect which column we are in and then use that information to determine what the course of action should be
    const qtyVal = getValue<QuantityWithUnits | number | string>();
    const { isHistoryDrawerOpen, diff } = useProjectHistory();
    const rows = table.getRowModel().rows;
    const orderId = row.original.thisOrderId;
    const textCellDiff = useMemo(() => {
      if (!isHistoryDrawerOpen || !diff) return;
      const entryKey = getAggEntryDiffPath(rows, row.id);
      const collectionKey = `project.orders.${orderId}.orderEntries.${entryKey}`;
      const diffKey = `${collectionKey}.${column.id}`;
      const diffVal = diff[diffKey];
      const typeOfqty = typeof qtyVal;
      if (typeOfqty === 'number' && diffVal) {
        return {
          old: diffVal.old,
          new: diffVal.new,
          isNumberDiff: typeOfqty === 'number',
        };
      }
      if (typeOfqty === 'string' && diffVal) {
        const oldParsedNum = Number.parseFloat(diffVal.old);
        const newParsedNum = Number.parseFloat(diffVal.new);
        if (!Number.isNaN(oldParsedNum) && !Number.isNaN(newParsedNum)) {
          const oldUnits = diffVal.old.replace(`${oldParsedNum}`, '').trim();
          const newUnits = diffVal.new.replace(`${newParsedNum}`, '').trim();
          if (oldUnits === newUnits) {
            return {
              old: oldParsedNum,
              new: newParsedNum,
              isNumberDiff: true,
            };
          }
        }
        return {
          old: diffVal.old,
          new: diffVal.new,
          isNumberDiff: false,
        };
      }
      if ((column.columnDef.meta as any)?.columnType === ColumnType.Custom) {
        return getQuantityAndCustomDiff({
          row,
          entryKey: collectionKey,
          diff,
          columnId: column.id,
          columnName: (column.columnDef.meta as any)?.name,
          resolvedValue: qtyVal as QuantityWithUnits | null,
        });
      }
      // Handle the label columns
      const lblKey = `${collectionKey}.parentOrder.labels`;
      const lblDiff = diff[lblKey];
      const tableId = (column.columnDef.meta as any)?.name;
      if (lblDiff && tableId) {
        // we need to know what the key is for the current column so that we can only extract its specific value
        const oldVals = JSON.parse(
          lblDiff.old || '[]'
        ) as OrderEntryTableRow['parentOrder']['labels'];
        const newVals = JSON.parse(
          lblDiff.new || '[]'
        ) as OrderEntryTableRow['parentOrder']['labels'];
        const oldValue = oldVals.find((lbl) => lbl.key === tableId)?.value;
        const newValue = newVals.find((lbl) => lbl.key === tableId)?.value;
        return {
          old: oldValue,
          new: newValue,
          isNumberDiff: false,
        };
      }
    }, [isHistoryDrawerOpen, diff, rows, row.id, orderId]);

    if (textCellDiff?.isNumberDiff) {
      return (
        <Box alignSelf="flex-end" paddingRight={2}>
          <DiffNumber
            newValue={textCellDiff.new as number | undefined}
            oldValue={textCellDiff.old as number | undefined}
          />
        </Box>
      );
    } else if (textCellDiff?.isNumberDiff === false) {
      return (
        <DiffText
          compact
          newValue={textCellDiff.new as string | undefined}
          oldValue={textCellDiff.old as string | undefined}
        />
      );
    }

    return (
      <Text noOfLines={1} wordBreak={'break-all'}>
        {getValue<string>()}
      </Text>
    );
  }
);

TextCell.displayName = 'TextCell';

const TableCell = memo(
  ({ getValue, row }: CellContext<OrderEntryTableRow, unknown>) => {
    const navigate = useNavigate();
    const value = getValue<string>();
    if (!value) return null;
    return (
      <Button
        variant={'outline'}
        colorScheme="gray"
        size="xs"
        wordBreak="break-all"
        height={4}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          navigate(
            `/project/${row.original.projectId}/takeoff/${row.original.parentOrder.id}`
          );
        }}
      >
        <CircleIcon
          color={orderStatusColorMapper(row.original.parentOrder.status)}
          marginRight={1}
        />
        {value}
      </Button>
    );
  }
);

TableCell.displayName = 'TableCell';

const QuantityCell = memo(
  ({
    getValue,
    table,
    row,
    column,
  }: CellContext<OrderEntryTableRow, unknown>) => {
    let formattedQuantity = '';
    const rawQuantity = getValue<QuantityWithUnits | string | number>();
    if (['string', 'number'].includes(typeof rawQuantity)) {
      formattedQuantity = (rawQuantity as string | number)?.toString() || '';
    } else {
      formattedQuantity =
        (rawQuantity && formatQuantity(rawQuantity as QuantityWithUnits)) || '';
    }

    const rows = table.getRowModel().rows;
    const orderId = row.original.thisOrderId;
    const { isHistoryDrawerOpen, diff } = useProjectHistory();
    const formattedValues = useMemo(() => {
      if (!isHistoryDrawerOpen || !diff) return;
      const subKey = getAggEntryDiffPath(rows, row.id);
      const entryKey = `project.orders.${orderId}.orderEntries.${subKey}`;
      const typeofQuantity = typeof rawQuantity;
      if (['string', 'number'].includes(typeofQuantity)) {
        // Assumption: we only enter this state if rawQty is string | number -> we can change this assumption later
        const groupedKey = `${entryKey}.resolvedQuantity`;
        const specificKey = `${groupedKey}.value`; // Not sure if we will hit this
        const diffVal = diff[groupedKey] || diff[specificKey];
        if (diffVal) {
          return {
            old: diffVal.old || 0,
            new: diffVal.new || 0,
            isNumberDiff: typeofQuantity === 'number',
          };
        }
      }
      return getQuantityAndCustomDiff({
        row,
        entryKey,
        diff,
        columnId: column.id,
        resolvedValue: rawQuantity as QuantityWithUnits | null,
      });
    }, [isHistoryDrawerOpen, diff, rows, orderId, row.id]);

    if (formattedValues?.isNumberDiff) {
      return (
        <Box alignSelf="flex-end" paddingRight={2}>
          <DiffNumber
            newValue={formattedValues.new as number | undefined}
            oldValue={formattedValues.old as number | undefined}
          />
        </Box>
      );
    } else if (formattedValues?.isNumberDiff === false) {
      return (
        <DiffText
          compact
          newValue={formattedValues.new as string | undefined}
          oldValue={formattedValues.old as string | undefined}
        />
      );
    }

    return (
      <Text
        noOfLines={1}
        wordBreak={'break-all'}
        textAlign={'end'}
        marginRight={1}
      >
        {formattedQuantity}
      </Text>
    );
  }
);

QuantityCell.displayName = 'QuantityCell';

const UnitCell = memo(
  ({ getValue, row, table }: CellContext<OrderEntryTableRow, unknown>) => {
    const unit = getValue<string>();
    const { isHistoryDrawerOpen, diff } = useProjectHistory();
    const orderId = row.original.thisOrderId;
    const rows = table.getRowModel().rows;
    const unitDiff = useMemo(() => {
      if (!isHistoryDrawerOpen || !diff) return;
      const subKey = getAggEntryDiffPath(rows, row.id);
      const unitDiffKey = `project.orders.${orderId}.orderEntries.${subKey}.unit`;
      const rawUnitDiff = diff[unitDiffKey];
      if (rawUnitDiff) {
        return {
          old: rawUnitDiff.old ? formatUnit(rawUnitDiff.old) : undefined,
          new: rawUnitDiff.new ? formatUnit(rawUnitDiff.new) : undefined,
        };
      }
    }, [isHistoryDrawerOpen, diff, row.id, orderId, rows]);

    if (unitDiff) {
      return <DiffText oldValue={unitDiff.old} newValue={unitDiff.new} />;
    }
    if (!unit) {
      return null;
    }
    return (
      <Text noOfLines={1} wordBreak={'break-all'}>
        {' '}
        {formatUnit(unit)}{' '}
      </Text>
    );
  }
);

UnitCell.displayName = 'UnitCell';

const ParentOrderStatusCell = memo(
  ({ getValue, row, table }: CellContext<OrderEntryTableRow, unknown>) => {
    const { isHistoryDrawerOpen, diff } = useProjectHistory();
    const status = getValue<OrderStatus>();
    const orderId = row.original.thisOrderId;
    const rows = table.getRowModel().rows;
    const statusDiff = useMemo(() => {
      if (!isHistoryDrawerOpen || !diff) return;
      const subKey = getAggEntryDiffPath(rows, row.id);
      const statusDiffKey = `project.orders.${orderId}.orderEntries.${subKey}.parentOrder.status`;
      return diff[statusDiffKey];
    }, [isHistoryDrawerOpen, diff, row.id, orderId, rows]);
    if (statusDiff) {
      return (
        <DiffText oldValue={statusDiff.old} newValue={statusDiff.new} compact />
      );
    }
    return status ? <OrderBadge orderStatus={getValue<OrderStatus>()} /> : null;
  }
);

ParentOrderStatusCell.displayName = 'ParentOrderStatusCell';

const CheckboxCell = ({ row }: CellContext<OrderEntryTableRow, unknown>) => {
  return (
    <Box display="inline-block" onClick={(e) => e.stopPropagation()}>
      <Checkbox
        aria-label={`Select row ${row.original.name}`}
        size="md"
        variant="rounded"
        isChecked={row.getIsSelected()}
        isIndeterminate={row.getIsSomeSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    </Box>
  );
};

type LinkedCountCellDiffingProps = {
  rows: Row<OrderEntryTableRow>[];
  row: Row<OrderEntryTableRow>;
  value: number;
  columnMeta: LinkedCountMeta;
  headerFontColor: string;
  translation: (x: string) => string;
};
function LinkedCountCell({
  columnMeta,
  rows,
  row,
  value,
  headerFontColor,
  translation,
}: LinkedCountCellDiffingProps) {
  const { isHistoryDrawerOpen, diff } = useProjectHistory();
  const orderId = row.original.thisOrderId;
  const linkedElementsDiff = useMemo(() => {
    if (!isHistoryDrawerOpen || !diff) return;
    const subKey = getAggEntryDiffPath(rows, row.id);
    const linkedElementKey = `project.orders.${orderId}.orderEntries.${subKey}.resolvedElementIdentifiersDbid`;
    const oldElementIds: Array<string> = [],
      newElementIds: Array<string> = [];
    const endfix = '.dbIds';
    for (const [key, value] of Object.entries(diff)) {
      if (!key.startsWith(linkedElementKey)) continue;
      for (const p of ['old', 'new']) {
        const arrLocation = key.endsWith(endfix)
          ? value?.[p]
          : value?.[p]?.dbIds;
        // carve out the model urn
        const modelUrn = key.slice(
          linkedElementKey.length,
          key.endsWith(endfix) ? key.length - endfix.length : undefined
        );
        const arr = JSON.parse(arrLocation || '[]') as Array<string>;
        const toInsert = arr.map((dbid) => `${modelUrn}-${dbid}`);
        p === 'new'
          ? newElementIds.push(...toInsert)
          : oldElementIds.push(...toInsert);
      }
    }
    const currentIds = row.original.resolvedElementIdentifiersDbid?.flatMap(
      (elementId) => {
        return elementId.dbIds.map((dbid) => `${elementId.modelUrn}-${dbid}`);
      }
    );
    if (oldElementIds.length || newElementIds.length) {
      return {
        old: oldElementIds,
        new: newElementIds,
        currentIds,
      };
    }
  }, [orderId, rows, diff, row.id, isHistoryDrawerOpen]);

  if (isHistoryDrawerOpen && linkedElementsDiff) {
    return (
      <DiffLinkedElementCount
        oldElements={linkedElementsDiff.old}
        newElements={linkedElementsDiff.new}
        currentIds={linkedElementsDiff.currentIds}
      />
    );
  }
  return value > 0 ? (
    <HStack color={headerFontColor} gap={1}>
      <Tooltip label={translation('select-linked-tooltip')} openDelay={400}>
        <Button
          aria-label={`${row.original.name}-linkedCount`}
          size="xs"
          variant="ghost"
          colorScheme="gray"
          alignSelf={'middle'}
          leftIcon={<SparkelIcon icon={faLink} size="xs" color="inherit" />}
          onClick={(e) => columnMeta?.onSelectLinkedElements?.(e, row)}
          color={headerFontColor}
          width={'fit-content'}
        >
          {value}
        </Button>
      </Tooltip>
    </HStack>
  ) : null;
}

const ExpanderCell = memo(
  ({ row }: CellContext<OrderEntryTableRow, unknown>) => {
    if (!row.getCanExpand()) {
      return null;
    }
    return (
      <IconButton
        marginLeft={2 * row.depth - 3}
        aria-label={`Toggle row expansion for ${row.original.name}`}
        icon={
          <SparkelIcon
            icon={row.getIsExpanded() ? faAngleDown : faAngleRight}
          />
        }
        variant="ghost"
        colorScheme="gray"
        size="xs"
        height={4}
        onClick={(e) => {
          // Prevent expansion from toggling row
          e.stopPropagation();
          row.toggleExpanded();
        }}
      />
    );
  }
);

ExpanderCell.displayName = 'ExapnderCell';

type AggregationComponentProps = {
  columnId: string;
  aggregation: Record<string, string>;
  updateAggregation: (
    action: 'delete' | 'update',
    columnId: string,
    value?: string
  ) => void;
};

const AggregationComponent = ({
  columnId,
  aggregation,
  updateAggregation,
}: AggregationComponentProps) => {
  const { t } = useTranslation('table');
  const title = t('column.aggregate-by-total');

  return (
    <HStack
      fontSize="xs"
      aria-label="Aggregation"
      paddingX="3"
      paddingY={1.5}
      gap={3}
    >
      <SparkelIcon as={SigmaIcon} />
      <span>{title}</span>
      <Spacer />
      <Switch
        id={columnId}
        size="sm"
        colorScheme="brand"
        isChecked={!!aggregation[columnId]}
        onChange={(e) => {
          const checked = e.target.checked;
          const action = checked ? 'update' : 'delete';
          const value = checked ? 'sum' : undefined;

          updateAggregation(action, columnId, value);
        }}
      />
    </HStack>
  );
};

const columnHelper = createColumnHelper<OrderEntryTableRow>();

// Extend the OrderColumnDeepFragment type to include originalKey
interface ExtendedOrderColumn extends OrderColumnDeepFragment {
  originalKey?: string;
}

// Define type for column mapping
type ColumnMapping = { id: string; newName: string };

// Define a function to get the display name for a column
const getDisplayName = (
  col: OrderColumnDeepFragment,
  columnNameMapping?: Record<string, ColumnMapping>,
  translator?: (key: string) => string
): string => {
  // If we have a mapping for this column ID, use the display name from the mapping
  if (columnNameMapping && columnNameMapping[col.id]) {
    return columnNameMapping[col.id].newName;
  }

  // If we have a translator and this is a standard column, translate it
  if (translator) {
    // Check if this is a special column that needs translation
    const specialColumns: Record<string, string> = {
      Title: 'title',
      Quantity: 'quantity',
      Unit: 'unit',
      Color: 'color',
      Table: 'parentOrder',
      'Table Status': 'parentOrderStatus',
      'Unit cost': 'unitPrice',
    };

    // If it's a special column name, use the translation
    if (col.name in specialColumns) {
      return translator(
        `table:default-column-name.${specialColumns[col.name]}`
      );
    }

    // For other columns that might be mapped to translation keys
    if (col.name in columnMapping) {
      return translator(`table:default-column-name.${columnMapping[col.name]}`);
    }
  }

  // Otherwise use the original name
  return col.name;
};

export const getColumns = (
  headerFontColor: string,
  thisOrderColumns: Array<ExtendedOrderColumn>,
  aggregation: Record<string, string>,
  grouping: Array<string>,
  updateAggregation: (
    action: 'delete' | 'update',
    columnId: string,
    value?: string
  ) => void,
  hideColumn: (x: string) => Promise<void>,
  renameColumn: (columnId: string | null, newName?: string) => void,
  translation: (x: string) => string,
  onSelectLinkedElements: (
    e: React.MouseEvent,
    row: Row<OrderEntryTableRow>
  ) => void,
  renamingColumnId: string | null,
  isHistoryMode = false,
  columnNameMapping?: Record<string, ColumnMapping>,
  isProtan?: boolean
) => {
  // Create the array with fixed utility columns first
  const columns: Array<ColumnDef<OrderEntryTableRow, unknown>> = [
    // Select column (always first)
    columnHelper.display({
      id: 'select', // Use 'select' as ID to match columnOrder
      size: 30,
      header: (props) => <RowSelectHeader {...props} />,
      cell: CheckboxCell,
      enableResizing: false,
      enableSorting: false,
      enableColumnFilter: false,
      enableGrouping: false,
    }),
    // Expander column (always second)
    columnHelper.display({
      id: 'expander', // Use 'expander' as ID to match columnOrder
      size: 4,
      minSize: 4,
      enableResizing: false,
      enableSorting: false,
      enableColumnFilter: false,
      enableGrouping: false,
      cell: ExpanderCell,
    }),
  ];

  // Include ALL columns, not just visible ones - visibility is controlled by the table state
  // Sort columns by their order
  const sortedColumns = [...thisOrderColumns].sort((a, b) => {
    const orderA = a.columnOrder ?? 1000;
    const orderB = b.columnOrder ?? 1000;
    return orderA - orderB;
  });

  // Create a mapping of columnId to columnDef
  const columnDefinitions: Record<string, ColumnDef<OrderEntryTableRow>> = {};

  // Process all columns in sorted order
  sortedColumns.forEach((col) => {
    let colDef;

    switch (col.columnType) {
      case ColumnType.Description:
        colDef = columnHelper.accessor('name', {
          header: () => (
            <ColumnHeader
              icon={<SparkelIcon as={ShapeLabelIcon} color="inherit" />}
              name={getDisplayName(col, columnNameMapping, translation)}
              isRenaming={col.id === renamingColumnId}
              onRename={(newName) => renameColumn(col.id, newName)}
              onRenameCancel={() => renameColumn(null)}
              translation={translation}
            />
          ),
          size: 300,
          id: 'title', // Use standardized table ID
          cell: TitleCell,
          meta: {
            dbColumnId: col.id, // Store the database ID for reference
            menuItems: (
              <>
                <MenuItem
                  aria-label={`Hide column ${getDisplayName(
                    col,
                    columnNameMapping,
                    translation
                  )}`}
                  icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                  onClick={() => hideColumn(col.id)}
                >
                  {translation('hide-column')}
                </MenuItem>
                <MenuItem
                  aria-label={`Rename column ${getDisplayName(
                    col,
                    columnNameMapping,
                    translation
                  )}`}
                  icon={<SparkelIcon icon={faPencil} fixedWidth />}
                  onClick={() => renameColumn(col.id)}
                >
                  {translation('column.rename')}
                </MenuItem>
              </>
            ),
          },
        });
        break;

      case ColumnType.Quantity:
        colDef = columnHelper.accessor('resolvedQuantity', {
          header: () => (
            <ColumnHeader
              icon={<SparkelIcon icon={faHashtag} color="inherit" />}
              name={getDisplayName(col, columnNameMapping, translation)}
              isRenaming={col.id === renamingColumnId}
              onRename={(newName) => renameColumn(col.id, newName)}
              onRenameCancel={() => renameColumn(null)}
              translation={translation}
            />
          ),
          id: columnTypeToId(ColumnType.Quantity), // Use standardized table ID
          meta: {
            dbColumnId: col.id, // Store the database ID for reference
            isNumeric: true,
            menuItems: (
              <>
                <MenuItem
                  aria-label={`Hide column ${getDisplayName(
                    col,
                    columnNameMapping,
                    translation
                  )}`}
                  icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                  onClick={() => hideColumn(col.id)}
                >
                  {translation('hide-column')}
                </MenuItem>
                <MenuItem
                  aria-label={`Rename column ${getDisplayName(
                    col,
                    columnNameMapping,
                    translation
                  )}`}
                  icon={<SparkelIcon icon={faPencil} fixedWidth />}
                  onClick={() => renameColumn(col.id)}
                >
                  {translation('column.rename')}
                </MenuItem>
                {!grouping.includes(col.id) && grouping.length > 0 ? (
                  <AggregationComponent
                    {...{ aggregation, updateAggregation }}
                    columnId={columnTypeToId(ColumnType.Quantity)}
                  />
                ) : null}
              </>
            ),
          },
          size: 100,
          cell: QuantityCell,
        });
        break;

      case ColumnType.Unit:
        colDef = columnHelper.accessor('unit', {
          header: () => (
            <ColumnHeader
              icon={<SparkelIcon icon={faRuler} color="inherit" />}
              name={getDisplayName(col, columnNameMapping, translation)}
              isRenaming={col.id === renamingColumnId}
              onRename={(newName) => renameColumn(col.id, newName)}
              onRenameCancel={() => renameColumn(null)}
              translation={translation}
            />
          ),
          id: 'unit', // Use standardized table ID
          size: 70,
          meta: {
            dbColumnId: col.id, // Store the database ID for reference
            menuItems: (
              <>
                <MenuItem
                  aria-label={`Hide column ${getDisplayName(
                    col,
                    columnNameMapping,
                    translation
                  )}`}
                  icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                  onClick={() => hideColumn(col.id)}
                >
                  {translation('hide-column')}
                </MenuItem>
                <MenuItem
                  aria-label={`Rename column ${getDisplayName(
                    col,
                    columnNameMapping,
                    translation
                  )}`}
                  icon={<SparkelIcon icon={faPencil} fixedWidth />}
                  onClick={() => renameColumn(col.id)}
                >
                  {translation('column.rename')}
                </MenuItem>
              </>
            ),
          },
          cell: UnitCell,
        });
        break;

      case ColumnType.Custom:
        colDef = columnHelper.accessor(
          (row) => {
            if ((row as any).__isGrouped) {
              return (row as any)[col.id];
            }
            // First try to find by column ID, which is more reliable after renaming
            const resolvedValue = row.customFields.find(
              (field) => field.orderColumnId === col.id
            )?.resolvedValue;

            // Fallback to finding by name if ID lookup fails
            if (!resolvedValue) {
              const byName = row.customFields.find(
                (field) => col.name === field.orderColumnName
              )?.resolvedValue;

              if (!byName) {
                return '';
              }
              return formatQuantity(byName);
            }

            return formatQuantity(resolvedValue);
          },
          {
            id: col.id, // For custom columns, use database ID directly
            minSize: 100,
            // If Protan and this custom column is literally named "Description",
            // make the default width larger
            size: isProtan && col.name === 'Description' ? 360 : undefined,
            header: () => (
              <ColumnHeader
                icon={<SparkelIcon icon={faGear} color="inherit" />}
                name={getDisplayName(col, columnNameMapping, translation)}
                isRenaming={col.id === renamingColumnId}
                onRename={(newName) => renameColumn(col.id, newName)}
                onRenameCancel={() => renameColumn(null)}
                translation={translation}
              />
            ),
            meta: {
              menuItems: (
                <>
                  <MenuItem
                    aria-label={`Hide column ${getDisplayName(
                      col,
                      columnNameMapping,
                      translation
                    )}`}
                    icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                    onClick={() => hideColumn(col.id)}
                  >
                    {translation('hide-column')}
                  </MenuItem>
                  <MenuItem
                    aria-label={`Rename column ${getDisplayName(
                      col,
                      columnNameMapping,
                      translation
                    )}`}
                    icon={<SparkelIcon icon={faPencil} fixedWidth />}
                    onClick={() => renameColumn(col.id)}
                  >
                    {translation('column.rename')}
                  </MenuItem>
                  {!grouping.includes(col.id) && grouping.length > 0 ? (
                    <AggregationComponent
                      {...{
                        updateAggregation,
                        aggregation,
                      }}
                      columnId={col.id}
                    />
                  ) : null}
                </>
              ),
              name: getDisplayName(col, columnNameMapping, translation),
              columnId: col.id, // Add ID to meta for easier reference
              dbColumnId: col.id, // Store the database ID for reference
              columnType: col.columnType, // we might have as well stored the entire column here :D
            },
            cell: TextCell,
          }
        );
        break;

      case ColumnType.ParentOrder:
        colDef = columnHelper.accessor('parentOrder.name', {
          header: () => (
            <ColumnHeader
              icon={<SparkelIcon icon={faTableList} color="inherit" />}
              name={getDisplayName(col, columnNameMapping, translation)}
              isRenaming={col.id === renamingColumnId}
              onRename={(newName) => renameColumn(col.id, newName)}
              onRenameCancel={() => renameColumn(null)}
              translation={translation}
            />
          ),
          id: 'parentOrder', // Use standardized table ID
          meta: {
            dbColumnId: col.id, // Store the database ID for reference
            menuItems: (
              <>
                <MenuItem
                  aria-label={`Hide column ${col.name}`}
                  icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                  onClick={() => hideColumn(col.id)}
                >
                  {translation('hide-column')}
                </MenuItem>
                <MenuItem
                  aria-label={`Rename column ${col.name}`}
                  icon={<SparkelIcon icon={faPencil} fixedWidth />}
                  onClick={() => renameColumn(col.id)}
                >
                  {translation('column.rename')}
                </MenuItem>
              </>
            ),
          },
          cell: TableCell,
        });
        break;

      case ColumnType.Color:
        colDef = columnHelper.accessor('color', {
          header: () => (
            <ColumnHeader
              icon={<SparkelIcon icon={faPalette} color="inherit" />}
              name={getDisplayName(col, columnNameMapping, translation)}
              isRenaming={col.id === renamingColumnId}
              onRename={(newName) => renameColumn(col.id, newName)}
              onRenameCancel={() => renameColumn(null)}
              translation={translation}
            />
          ),
          id: 'color', // Use standardized table ID
          size: 60,
          enableSorting: false,
          enableColumnFilter: false,
          meta: {
            dbColumnId: col.id, // Store the database ID for reference
            menuItems: (
              <>
                <MenuItem
                  aria-label={`Hide column ${col.name}`}
                  icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                  onClick={() => hideColumn(col.id)}
                >
                  {translation('hide-column')}
                </MenuItem>
                <MenuItem
                  aria-label={`Rename column ${col.name}`}
                  icon={<SparkelIcon icon={faPencil} fixedWidth />}
                  onClick={() => renameColumn(col.id)}
                >
                  {translation('column.rename')}
                </MenuItem>
              </>
            ),
          },
          cell: ({ getValue }) => (
            <Box
              height={'16px'}
              width={'16px'}
              borderRadius={'full'}
              background={getValue<string>()}
            />
          ),
        });
        break;

      case ColumnType.LinkedCount:
        colDef = columnHelper.accessor('linkedCount', {
          header: () => <SparkelIcon icon={faLink} color="inherit" />,
          id: 'linkedCount', // Use standardized table ID
          size: 60,
          enableSorting: false,
          meta: {
            dbColumnId: col.id, // Store the database ID for reference
            onSelectLinkedElements,
            menuItems: (
              <MenuItem
                aria-label={`Hide column ${col.name}`}
                icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                onClick={() => hideColumn(col.id)}
              >
                {translation('hide-column')}
              </MenuItem>
            ),
          },
          cell: ({ getValue, row, column, table }) => {
            const value = getValue<number>();
            const columnMeta = column.columnDef.meta as LinkedCountMeta;
            const rows = table.getRowModel().rows;
            return (
              <LinkedCountCell
                {...{
                  value,
                  columnMeta,
                  headerFontColor,
                  translation,
                  row,
                  rows,
                }}
              />
            );
          },
        });
        break;

      case ColumnType.ParentOrderStatus:
        colDef = columnHelper.accessor('parentOrder.status', {
          header: () => (
            <ColumnHeader
              icon={<SparkelIcon icon={faCircle} color="inherit" />}
              name={getDisplayName(col, columnNameMapping, translation)}
              isRenaming={col.id === renamingColumnId}
              onRename={(newName) => renameColumn(col.id, newName)}
              onRenameCancel={() => renameColumn(null)}
              translation={translation}
            />
          ),
          id: 'parentOrderStatus', // Use standardized table ID
          meta: {
            dbColumnId: col.id, // Store the database ID for reference
            menuItems: (
              <>
                <MenuItem
                  aria-label={`Hide column ${col.name}`}
                  icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                  onClick={() => hideColumn(col.id)}
                >
                  {translation('hide-column')}
                </MenuItem>
                <MenuItem
                  aria-label={`Rename column ${col.name}`}
                  icon={<SparkelIcon icon={faPencil} fixedWidth />}
                  onClick={() => renameColumn(col.id)}
                >
                  {translation('column.rename')}
                </MenuItem>
              </>
            ),
          },
          cell: ParentOrderStatusCell,
        });
        break;

      case ColumnType.UnitPrice:
        colDef = columnHelper.accessor('unitPrice', {
          header: () => (
            <HStack>
              <SparkelIcon icon={faCoins} color="inherit" />
              <>{translation('default-column-name.unitPrice')}</>
            </HStack>
          ),
          id: 'unitPrice', // Use standardized table ID
          meta: {
            dbColumnId: col.id, // Store the database ID for reference
            menuItems: (
              <MenuItem
                aria-label={`Hide column ${col.name}`}
                icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                onClick={() => hideColumn(col.id)}
              >
                {translation('hide-column')}
              </MenuItem>
            ),
          },
          cell: UnitPriceCell,
        });
        break;

      case ColumnType.Total:
        colDef = columnHelper.accessor('total', {
          header: () => (
            <HStack>
              <SparkelIcon as={SigmaIcon} color="inherit" />
              <>Total</>
            </HStack>
          ),
          id: 'total', // Use standardized table ID
          meta: {
            dbColumnId: col.id, // Store the database ID for reference
            menuItems: (
              <MenuItem
                aria-label={`Hide column ${col.name}`}
                icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                onClick={() => hideColumn(col.id)}
              >
                {translation('hide-column')}
              </MenuItem>
            ),
          },
          cell: TotalPriceCell,
        });
        break;

      case ColumnType.ParentOrderLabel:
        colDef = columnHelper.accessor(
          (row) => {
            const labelValue = row.parentOrder.labels.find(
              (label) => col.name === label.key
            )?.value;
            if (!labelValue) {
              return '';
            }
            return labelValue;
          },
          {
            id: col.id, // For ParentOrderLabel columns, use database ID directly
            size: 300,
            header: () => (
              <ColumnHeader
                icon={<SparkelIcon icon={faTag} color="inherit" />}
                name={getDisplayName(col, columnNameMapping, translation)}
                isRenaming={col.id === renamingColumnId}
                onRename={(newName) => renameColumn(col.id, newName)}
                onRenameCancel={() => renameColumn(null)}
                translation={translation}
              />
            ),
            meta: {
              menuItems: (
                <>
                  <MenuItem
                    aria-label={`Hide column ${col.name}`}
                    icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                    onClick={() => hideColumn(col.id)}
                  >
                    {translation('hide-column')}
                  </MenuItem>
                  <MenuItem
                    aria-label={`Rename column ${col.name}`}
                    icon={<SparkelIcon icon={faPencil} fixedWidth />}
                    onClick={() => renameColumn(col.id)}
                  >
                    {translation('column.rename')}
                  </MenuItem>
                </>
              ),
              dbColumnId: col.id, // Store the database ID for reference
              columnType: ColumnType.ParentOrderLabel,
              name: col.name,
            },
            cell: TextCell,
          }
        );
        break;

      case ColumnType.ItemGroup:
        {
          // For item groups, we always use the original column name for data access
          // but display the renamed name from the mapping
          const originalKey = col.name;

          colDef = columnHelper.accessor(
            (row) => {
              // Always use the original column name to look up data
              const correspondingGroup = row.itemGroups?.find(
                (itemGroup) => itemGroup[originalKey]
              );

              if (!correspondingGroup) return '';

              return correspondingGroup[originalKey] ?? '';
            },
            {
              id: col.id, // For ItemGroup columns, use database ID directly
              minSize: 100,
              header: () => (
                <ColumnHeader
                  icon={<SparkelIcon icon={faSitemap} color="inherit" />}
                  name={getDisplayName(col, columnNameMapping, translation)}
                  isRenaming={col.id === renamingColumnId}
                  onRename={(newName) => renameColumn(col.id, newName)}
                  onRenameCancel={() => renameColumn(null)}
                  translation={translation}
                />
              ),
              meta: {
                menuItems: (
                  <>
                    <MenuItem
                      aria-label={`Hide column ${getDisplayName(
                        col,
                        columnNameMapping,
                        translation
                      )}`}
                      icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                      onClick={() => hideColumn(col.id)}
                    >
                      {translation('hide-column')}
                    </MenuItem>
                    <MenuItem
                      aria-label={`Rename column ${getDisplayName(
                        col,
                        columnNameMapping,
                        translation
                      )}`}
                      icon={<SparkelIcon icon={faPencil} fixedWidth />}
                      onClick={() => renameColumn(col.id)}
                    >
                      {translation('rename.column')}
                    </MenuItem>
                  </>
                ),
                name: getDisplayName(col, columnNameMapping, translation),
                columnId: col.id,
                originalKey, // Store the original key for data access
                dbColumnId: col.id, // Store the database ID for reference
              },
              cell: TextCell,
            }
          );
        }
        break;
    }

    if (colDef) {
      // Get the correct ID for storing in columnDefinitions
      let defId = col.id;

      // For default columns, use the standardized table ID
      if (
        ![
          ColumnType.Custom,
          ColumnType.ParentOrderLabel,
          ColumnType.ItemGroup,
        ].includes(col.columnType)
      ) {
        const tableId = columnTypeToId(
          col.columnType as
            | ColumnType.Color
            | ColumnType.Description
            | ColumnType.LinkedCount
            | ColumnType.ParentOrder
            | ColumnType.ParentOrderStatus
            | ColumnType.Quantity
            | ColumnType.Total
            | ColumnType.Unit
            | ColumnType.UnitPrice
        );
        if (tableId) {
          defId = tableId;
        }
      }

      // Store the column definition in the mapping
      columnDefinitions[defId] = colDef as ColumnDef<OrderEntryTableRow>;
      columns.push(colDef as ColumnDef<OrderEntryTableRow>);
    }
  });

  if (isHistoryMode) {
    columns.push(
      columnHelper.display({
        id: 'historyDisplay',
        maxSize: 50,
        cell: () => null,
      })
    );
  }
  return columns;
};
